import * as Headless from "@headlessui/react";
import clsx from "clsx";
import React, { forwardRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { TouchTarget } from "@/components/ui/button/Button";
import { Link } from "@/components/ui/link/Link";
import {
  STATUS_AGENT_SURFACE,
  STATUS_DANGER_SURFACE,
  STATUS_INFO_SURFACE,
  STATUS_SUCCESS_SURFACE,
  STATUS_WARNING_SURFACE,
} from "@/components/ui/surface/surfaceTokens";

const colors = {
  red: "bg-red-500/15 text-red-700 group-data-hover:bg-red-500/25 dark:bg-red-500/10 dark:text-red-400 dark:group-data-hover:bg-red-500/20",
  orange:
    "bg-orange-500/15 text-orange-700 group-data-hover:bg-orange-500/25 dark:bg-orange-500/10 dark:text-orange-400 dark:group-data-hover:bg-orange-500/20",
  amber:
    "bg-amber-400/20 text-amber-700 group-data-hover:bg-amber-400/30 dark:bg-amber-400/10 dark:text-amber-400 dark:group-data-hover:bg-amber-400/15",
  yellow:
    "bg-yellow-400/20 text-yellow-700 group-data-hover:bg-yellow-400/30 dark:bg-yellow-400/10 dark:text-yellow-300 dark:group-data-hover:bg-yellow-400/15",
  lime: "bg-lime-400/20 text-lime-700 group-data-hover:bg-lime-400/30 dark:bg-lime-400/10 dark:text-lime-300 dark:group-data-hover:bg-lime-400/15",
  green:
    "bg-green-500/15 text-green-700 group-data-hover:bg-green-500/25 dark:bg-green-500/10 dark:text-green-400 dark:group-data-hover:bg-green-500/20",
  emerald:
    "bg-emerald-500/15 text-emerald-700 group-data-hover:bg-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-400 dark:group-data-hover:bg-emerald-500/20",
  teal: "bg-teal-500/15 text-teal-700 group-data-hover:bg-teal-500/25 dark:bg-teal-500/10 dark:text-teal-300 dark:group-data-hover:bg-teal-500/20",
  cyan: "bg-cyan-400/20 text-cyan-700 group-data-hover:bg-cyan-400/30 dark:bg-cyan-400/10 dark:text-cyan-300 dark:group-data-hover:bg-cyan-400/15",
  sky: "bg-sky-500/15 text-sky-700 group-data-hover:bg-sky-500/25 dark:bg-sky-500/10 dark:text-sky-300 dark:group-data-hover:bg-sky-500/20",
  blue: "bg-indigo-400/15 text-indigo-700 group-data-hover:bg-indigo-400/25 dark:bg-indigo-400/10 dark:text-indigo-400 dark:group-data-hover:bg-indigo-400/25",
  indigo:
    "bg-indigo-400/15 text-indigo-700 group-data-hover:bg-indigo-400/25 dark:text-indigo-400 dark:group-data-hover:bg-indigo-400/20",
  violet:
    "bg-violet-500/15 text-violet-700 group-data-hover:bg-violet-500/25 dark:text-violet-400 dark:group-data-hover:bg-violet-500/20",
  purple:
    "bg-purple-500/15 text-purple-700 group-data-hover:bg-purple-500/25 dark:text-purple-400 dark:group-data-hover:bg-purple-500/20",
  fuchsia:
    "bg-fuchsia-400/15 text-fuchsia-700 group-data-hover:bg-fuchsia-400/25 dark:bg-fuchsia-400/10 dark:text-fuchsia-400 dark:group-data-hover:bg-fuchsia-400/20",
  pink: "bg-pink-400/15 text-pink-700 group-data-hover:bg-pink-400/25 dark:bg-pink-400/10 dark:text-pink-400 dark:group-data-hover:bg-pink-400/20",
  rose: "bg-rose-400/15 text-rose-700 group-data-hover:bg-rose-400/25 dark:bg-rose-400/10 dark:text-rose-400 dark:group-data-hover:bg-rose-400/20",
  zinc: "bg-slate-600/10 text-slate-700 group-data-hover:bg-slate-600/20 dark:bg-white/5 dark:text-slate-400 dark:group-data-hover:bg-white/10",
  /** Queued / pipeline — matches meetings Mission Control status tone */
  slate:
    "bg-slate-500/18 text-slate-800 group-data-hover:bg-slate-500/28 dark:bg-slate-700/58 dark:text-slate-200 dark:group-data-hover:bg-slate-700/68",
  /** Auto-closed / terminal-neutral — dimmer label on same family as `slate` */
  slateMuted:
    "bg-slate-500/15 text-slate-700 group-data-hover:bg-slate-500/24 dark:bg-slate-700/52 dark:text-slate-400 dark:group-data-hover:bg-slate-700/62",
  /** Status pills — base colours from `STATUS_*_SURFACE` plus hover. */
  statusWarning: clsx(
    STATUS_WARNING_SURFACE,
    "group-data-hover:bg-amber-400/30 dark:group-data-hover:bg-amber-400/15",
  ),
  statusSuccess: clsx(
    STATUS_SUCCESS_SURFACE,
    "group-data-hover:bg-emerald-500/25 dark:group-data-hover:bg-emerald-500/20",
  ),
  statusDanger: clsx(
    STATUS_DANGER_SURFACE,
    "group-data-hover:bg-red-500/25 dark:group-data-hover:bg-red-500/20",
  ),
  statusInfo: clsx(
    STATUS_INFO_SURFACE,
    "group-data-hover:bg-slate-500/28 dark:group-data-hover:bg-slate-700/68",
  ),
  statusAgent: clsx(
    STATUS_AGENT_SURFACE,
    "group-data-hover:bg-indigo-400/25 dark:group-data-hover:bg-indigo-400/25",
  ),
};

export { colors };

export type BadgeColor = keyof typeof colors;

export type BadgeVariant = "status" | "chip";

type BadgeProps = {
  color?: BadgeColor;
  size?: "sm" | "md";
  /** Status labels (mono pill) vs context chips (sentence case, optional dismiss). */
  variant?: BadgeVariant;
  /** When set, renders a remove control — use with `variant="chip"` for composer context. */
  onDismiss?: () => void;
  dismissLabel?: string;
};

function dismissAriaLabel(children: React.ReactNode, dismissLabel?: string) {
  if (dismissLabel) return dismissLabel;
  if (typeof children === "string" || typeof children === "number") {
    return `Remove ${children}`;
  }
  return "Remove";
}

/** `truncate` on the outer flex shell breaks vertical centering — route it to the label. */
function splitTruncateClass(className?: string): { className: string; truncate: boolean } {
  const value = className ?? "";
  const truncate = /\btruncate\b/.test(value);
  const stripped = value.replace(/\btruncate\b/g, "").replace(/\s+/g, " ").trim();
  return { className: stripped, truncate };
}

function BadgeLabel({
  children,
  truncate,
  isChip,
}: {
  children: React.ReactNode;
  truncate: boolean;
  isChip: boolean;
}) {
  if (isChip) {
    return truncate ? <span className="min-w-0 truncate">{children}</span> : children;
  }

  return (
    <span className={clsx("min-w-0 leading-none", truncate && "truncate")}>{children}</span>
  );
}

export function Badge({
  color = "zinc",
  size = "md",
  variant = "status",
  onDismiss,
  dismissLabel,
  className,
  children,
  ...props
}: BadgeProps & React.ComponentPropsWithoutRef<"span">) {
  const isChip = variant === "chip";
  const { className: outerClassName, truncate } = splitTruncateClass(className);

  return (
    <span
      {...props}
      className={clsx(
        outerClassName,
        "inline-flex items-center justify-center font-medium forced-colors:outline",
        isChip
          ? "gap-1 rounded-md normal-case tracking-normal"
          : "gap-x-1.5 rounded-full font-mono uppercase tracking-widest",
        size === "sm"
          ? isChip
            ? "min-h-5 px-2 py-0.5 text-xs leading-tight"
            : "h-5 px-2 text-[10px]"
          : isChip
            ? "min-h-6 px-2.5 py-1 text-sm leading-tight"
            : "h-6 px-2.5 text-xs",
        colors[color],
      )}
    >
      {onDismiss ? (
        <span className="min-w-0 truncate">{children}</span>
      ) : (
        <BadgeLabel truncate={truncate} isChip={isChip}>{children}</BadgeLabel>
      )}
      {onDismiss ? (
        <Headless.Button
          type="button"
          onClick={onDismiss}
          className={clsx(
            "shrink-0 rounded p-0.5 opacity-70 transition-opacity hover:opacity-100",
            "focus:outline-hidden data-focus:outline-2 data-focus:outline-offset-1 data-focus:outline-indigo-400",
          )}
          aria-label={dismissAriaLabel(children, dismissLabel)}
        >
          <FontAwesomeIcon icon={faXmark} className="text-[10px]" />
        </Headless.Button>
      ) : null}
    </span>
  );
}

export const BadgeButton = forwardRef(function BadgeButton(
  {
    color = "zinc",
    className,
    children,
    ...props
  }: BadgeProps & { className?: string; children: React.ReactNode } & (
      | Omit<Headless.ButtonProps, "as" | "className">
      | Omit<React.ComponentPropsWithoutRef<typeof Link>, "className">
    ),
  ref: React.ForwardedRef<HTMLElement>
) {
  const classes = clsx(
    className,
    "group relative inline-flex rounded-md focus:outline-hidden data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-indigo-400"
  );

  return "href" in props ? (
    <Link
      {...props}
      className={classes}
      ref={ref as React.ForwardedRef<HTMLAnchorElement>}
    >
      <TouchTarget>
        <Badge color={color}>{children}</Badge>
      </TouchTarget>
    </Link>
  ) : (
    <Headless.Button {...props} className={classes} ref={ref}>
      <TouchTarget>
        <Badge color={color}>{children}</Badge>
      </TouchTarget>
    </Headless.Button>
  );
});
